/**
 * ProjectsGroupedView
 *
 * Renders the project list as collapsible groups when a Group By field is active.
 * Each group has:
 *   - A sticky header row showing the group label, project count and key subtotals
 *   - The normal ProjectsGrid rows for that group's projects
 *   - A subtotal footer row showing numeric sums for visible financial/resource columns
 *
 * Row loading has two modes:
 *   - Server-aggregated groups (live data): counts and subtotals come from the
 *     grouped-tree endpoint and each leaf group loads its own page of rows on expand,
 *     so the full project dataset never reaches the browser.
 *   - Client-side groups (demo/tour, or grouped-tree fallback): `group.projects` is
 *     already populated and is rendered directly.
 *
 * Server-aggregated groups are themselves paged: each sibling set (the top-level groups,
 * and each expanded parent's children) loads one page at a time, so grouping 50k projects
 * by client renders a page of clients rather than all 5k of them.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SensitiveData } from '@/components/sensitive-data';
import {
  formatCurrency,
  formatPercent,
  healthToVariant,
  getProjectTypeColor,
  formatProjectType,
} from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { Skeleton } from '@/components/ui/skeleton';
import { getProjectStatusColor } from '@/lib/status-colors';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import type {
  ProjectGroup,
  GroupById,
  GroupByRefData,
  GroupPathSegment,
} from '@/lib/project-group-by-utils';
import { getGroupHeaderColumns } from '@/hooks/use-group-header-settings';
import { getProjectColumnValue } from '@/components/project-filter-sheet';
import type { Project, Client, Employee, Task, Milestone, ResourceAllocation } from '@/types';
import type { PaginationMetadata } from '@/types/pagination';
import { fetchProjects, type ProjectFilterParams } from '@/lib/data-service';
import { groupPathToFilterParams } from '@/lib/project-group-by-utils';
import type { ColumnDefinition } from '@/components/column-configurator-drawer';
import type { ColumnSort } from '@/components/ui/data-table-enhanced';
import { ProjectsGrid, type ProjectsGridReferenceData } from '@/components/projects-grid';
import { PaginationControls } from '@/components/pagination-controls';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { useProjectGroupLevel } from '@/hooks/use-project-group-level';

// ─── Subtotal columns (those that are summed in the footer row) ───────

const SUBTOTAL_COLUMN_IDS = new Set([
  'budget',
  'actual-sales',
  'sales-variance',
  'cost-budget',
  'actual-cost',
  'cost-variance',
  'gross-profit',
  'wip-amount',
  'forecast-at-completion',
  'allocated-hours',
  'actual-hours',
  'hours-variance',
]);

// ─── Group header badge ───────────────────────────────────────────────

function GroupHeaderBadge({ groupBy, groupKey }: { groupBy: GroupById; groupKey: string }) {
  const activeGroupBy = groupBy;
  const { statusLabels } = useProjectStatuses();

  if (groupKey === '__none__') return null;

  if (activeGroupBy === 'health-indicator') {
    const labelMap: Record<string, string> = { green: 'Healthy', amber: 'At Risk', red: 'Critical' };
    return (
      <Badge variant={healthToVariant(groupKey as 'green' | 'amber' | 'red')} className="text-xs gap-1.5" disableHover>
        <StatusIndicator variant={groupKey === 'green' ? 'success' : groupKey === 'amber' ? 'warning' : 'error'} size="sm" />
        {labelMap[groupKey] ?? groupKey}
      </Badge>
    );
  }

  if (activeGroupBy === 'project-type' && groupKey !== '__none__') {
    return (
      <Badge variant="outline" className={cn(getProjectTypeColor(groupKey as Parameters<typeof getProjectTypeColor>[0]), 'text-xs')} disableHover>
        {formatProjectType(groupKey as Parameters<typeof formatProjectType>[0])}
      </Badge>
    );
  }

  if (activeGroupBy === 'project-status') {
    const statusId = parseInt(groupKey, 10);
    const label = statusLabels[statusId] ?? `Status ${groupKey}`;
    return (
      <Badge variant="outline" className={cn(getProjectStatusColor(statusId), 'text-xs')} disableHover>
        {label}
      </Badge>
    );
  }

  return null;
}

// ─── Subtotal row ─────────────────────────────────────────────────────

interface SubtotalRowProps {
  group: ProjectGroup;
  visibleColumnIds: string[];
}

function SubtotalRow({ group, visibleColumnIds }: SubtotalRowProps) {
  const { subtotals } = group;
  const hasAnySubtotal = visibleColumnIds.some((id) => SUBTOTAL_COLUMN_IDS.has(id));
  if (!hasAnySubtotal) return null;

  const renderCell = (colId: string) => {
    if (!SUBTOTAL_COLUMN_IDS.has(colId)) {
      // Non-subtotal columns get an empty placeholder
      if (colId === 'project-name') {
        return (
          <td key={colId} className="px-4 py-2 text-xs font-semibold text-muted-foreground">
            Subtotals ({subtotals.count} {subtotals.count === 1 ? 'project' : 'projects'})
          </td>
        );
      }
      return <td key={colId} className="px-4 py-2" />;
    }

    let value: React.ReactNode = null;
    switch (colId) {
      case 'budget':
        value = formatCurrency(subtotals.budgetRevenue);
        break;
      case 'actual-sales':
        value = formatCurrency(subtotals.actualRevenue);
        break;
      case 'sales-variance':
        value = <span className="text-muted-foreground text-xs italic">—</span>;
        break;
      case 'cost-budget':
        value = formatCurrency(subtotals.costBudget);
        break;
      case 'actual-cost':
        value = formatCurrency(subtotals.actualCost);
        break;
      case 'cost-variance':
        value = <span className="text-muted-foreground text-xs italic">—</span>;
        break;
      case 'gross-profit': {
        const gp = subtotals.grossProfit;
        value = (
          <span className={gp >= 0 ? 'text-success-600' : 'text-error-600'}>
            {formatCurrency(gp)}
          </span>
        );
        break;
      }
      case 'wip-amount':
        value = formatCurrency(subtotals.wipAmount);
        break;
      case 'allocated-hours':
        value = `${subtotals.budgetHours.toLocaleString()} hrs`;
        break;
      case 'actual-hours':
        value = `${subtotals.actualHours.toLocaleString()} hrs`;
        break;
      case 'hours-variance': {
        const hv = subtotals.budgetHours - subtotals.actualHours;
        value = (
          <span className={hv >= 0 ? 'text-success-600' : 'text-error-600'}>
            {hv >= 0 ? '+' : ''}{hv.toLocaleString()} hrs
          </span>
        );
        break;
      }
      case 'forecast-at-completion':
        value = <span className="text-muted-foreground text-xs italic">—</span>;
        break;
      default:
        value = null;
    }

    return (
      <td key={colId} className="px-4 py-2 text-right text-xs font-semibold text-foreground">
        <SensitiveData>{value}</SensitiveData>
      </td>
    );
  };

  return (
    <tr className="bg-muted/30 border-t border-b border-border">
      {visibleColumnIds.map((colId) => renderCell(colId))}
    </tr>
  );
}

// ─── Group header summary (driven by company settings) ───────────────

function getSubtotalValue(
  colId: string,
  subtotals: ProjectGroup['subtotals'],
): { value: number; isGP: boolean } | null {
  switch (colId) {
    case 'budget':             return { value: subtotals.budgetRevenue, isGP: false };
    case 'actual-sales':       return { value: subtotals.actualRevenue, isGP: false };
    case 'sales-variance':     return { value: subtotals.budgetRevenue - subtotals.actualRevenue, isGP: false };
    case 'cost-budget':        return { value: subtotals.costBudget, isGP: false };
    case 'actual-cost':        return { value: subtotals.actualCost, isGP: false };
    case 'cost-variance':      return { value: subtotals.costBudget - subtotals.actualCost, isGP: false };
    case 'gross-profit':       return { value: subtotals.grossProfit, isGP: true };
    case 'wip-amount':         return { value: subtotals.wipAmount, isGP: false };
    case 'forecast-at-completion': return null; // derived — not meaningful as a simple sum
    case 'allocated-hours':    return null; // hours — needs different formatting
    case 'actual-hours':       return null;
    case 'hours-variance':     return null;
    default:                   return null;
  }
}

function getSubtotalHours(
  colId: string,
  subtotals: ProjectGroup['subtotals'],
): number | null {
  switch (colId) {
    case 'allocated-hours': return subtotals.budgetHours;
    case 'actual-hours':    return subtotals.actualHours;
    case 'hours-variance':  return subtotals.budgetHours - subtotals.actualHours;
    default:                return null;
  }
}

interface GroupHeaderSummaryProps {
  group: ProjectGroup;
  columnDefinitions: ColumnDefinition[];
}

function GroupHeaderSummary({ group, columnDefinitions }: GroupHeaderSummaryProps) {
  // Read the company-level setting (from localStorage) — stable across renders
  const headerColumnIds = getGroupHeaderColumns();

  if (headerColumnIds.length === 0) return null;

  // Build a label map from the live column definitions (exact same text as column headers)
  const labelMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const def of columnDefinitions) {
      if (def.label) map[def.id] = def.label;
    }
    return map;
  }, [columnDefinitions]);

  return (
    <div className="hidden sm:flex items-center gap-4 shrink-0">
      {headerColumnIds.map((colId) => {
        const label = labelMap[colId] ?? colId;
        const currency = getSubtotalValue(colId, group.subtotals);
        const hours    = getSubtotalHours(colId, group.subtotals);

        if (currency === null && hours === null) return null;

        return (
          <div key={colId} className="text-right">
            <p className="text-[10px] text-muted-foreground leading-none">{label}</p>
            <SensitiveData>
              {currency !== null ? (
                <p
                  className={cn(
                    'text-xs font-medium',
                    currency.isGP
                      ? currency.value >= 0
                        ? 'text-success-600'
                        : 'text-error-600'
                      : '',
                  )}
                >
                  {formatCurrency(currency.value)}
                </p>
              ) : (
                <p className="text-xs font-medium">
                  {hours!.toLocaleString()} hrs
                </p>
              )}
            </SensitiveData>
          </div>
        );
      })}
    </div>
  );
}

// ─── Single group block ───────────────────────────────────────────────

interface GroupBlockProps {
  group: ProjectGroup;
  allGroupBy: GroupById[];
  referenceData: ProjectsGridReferenceData;
  columnDefinitions: ColumnDefinition[];
  columnSorts: ColumnSort[];
  onColumnSortChange: (sorts: ColumnSort[]) => void;
  onRowClick: (project: Project) => void;
  collapsed: Set<string>;
  onToggleCollapse: (key: string) => void;
  /** List-level filters (search / status / health / sort / scope) applied to leaf row requests */
  leafFilters?: ProjectFilterParams;
  /** Initial page size for server-paginated leaf groups */
  leafPageSize?: number;
  /** Set when sibling sets are paged by the server; children are fetched on expand. */
  serverLevels?: ServerLevelContext;
}

/** Everything a server-paged sibling set needs in order to fetch itself. */
export interface ServerLevelContext {
  groupBy: GroupById[];
  groupRefData: GroupByRefData;
  /** List-level filters scoping the whole tree (not a single leaf's group filters). */
  filters?: ProjectFilterParams;
  pageSize: number;
}

/** Orders a page of projects by the active column sorts. */
function sortProjectsByColumns(
  projects: Project[],
  columnSorts: ColumnSort[],
  clients: Client[],
): Project[] {
  if (columnSorts.length === 0) return projects;
  const refData = { clients };
  return [...projects].sort((a, b) => {
    for (const { columnId, direction } of columnSorts) {
      const aVal = getProjectColumnValue(columnId, a, refData);
      const bVal = getProjectColumnValue(columnId, b, refData);
      if (aVal === undefined && bVal === undefined) continue;
      if (aVal === undefined) return 1;
      if (bVal === undefined) return -1;
      let cmp = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' });
      }
      if (cmp !== 0) return direction === 'asc' ? cmp : -cmp;
    }
    return 0;
  });
}

// ─── Server-paginated leaf grid ───────────────────────────────────

interface ServerLeafGridProps {
  group: ProjectGroup;
  leafFilters?: ProjectFilterParams;
  initialPageSize: number;
  referenceData: ProjectsGridReferenceData;
  columnDefinitions: ColumnDefinition[];
  columnSorts: ColumnSort[];
  onColumnSortChange: (sorts: ColumnSort[]) => void;
  onRowClick: (project: Project) => void;
  visibleColumnIds: string[];
}

/**
 * Loads and renders one page of a leaf group's projects. The group's own key path
 * (plus the list-level filters) is sent to the paginated project endpoint, so only
 * the rows on screen are transferred.
 */
function ServerLeafGrid({
  group,
  leafFilters,
  initialPageSize,
  referenceData,
  columnDefinitions,
  columnSorts,
  onColumnSortChange,
  onRowClick,
  visibleColumnIds,
}: ServerLeafGridProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [rows, setRows] = useState<Project[]>([]);
  const [pagination, setPagination] = useState<PaginationMetadata | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const { handleApiError } = useErrorHandler({ useToast: true });

  // The concrete filters that isolate this leaf's rows: the group value of every
  // ancestor level, layered on top of the list-level filters.
  const requestFilters = useMemo<ProjectFilterParams>(() => {
    const groupFilters = groupPathToFilterParams(group.groupPath ?? []);
    return {
      ...leafFilters,
      statusId: groupFilters.statusId ?? leafFilters?.statusId,
      health: groupFilters.health ?? leafFilters?.health,
      managerId: groupFilters.managerId ?? leafFilters?.managerId,
      clientId: groupFilters.clientId ?? leafFilters?.clientId,
      type: groupFilters.type,
      unassigned: groupFilters.unassigned.length > 0 ? groupFilters.unassigned : undefined,
    };
  }, [group.groupPath, leafFilters]);

  const requestKey = useMemo(() => JSON.stringify(requestFilters), [requestFilters]);

  // Filters changed — restart from the first page of this leaf.
  useEffect(() => {
    setPage(1);
  }, [requestKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetchProjects('projects', { page, pageSize }, requestFilters)
      .then((result) => {
        if (cancelled) return;
        setLoadFailed(false);
        if (result && typeof result === 'object' && 'data' in result && 'pagination' in result) {
          setRows(result.data);
          setPagination(result.pagination);
        } else {
          setRows(Array.isArray(result) ? result : []);
          setPagination(undefined);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        handleApiError(error, '/api/projects', 'GET');
        setRows([]);
        setPagination(undefined);
        setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [requestKey, page, pageSize, retryToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only the current page is held locally, so client-side sorts order the page.
  const sortedRows = useMemo(
    () => sortProjectsByColumns(rows, columnSorts, referenceData.clients),
    [rows, columnSorts, referenceData.clients],
  );

  if (loadFailed && !loading) {
    return (
      <div className="flex items-center gap-3 px-4 py-6 text-sm text-muted-foreground">
        <span>Couldn&apos;t load the projects in this group.</span>
        <Button variant="outline" size="sm" onClick={() => setRetryToken((token) => token + 1)}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <ProjectsGrid
      data={sortedRows}
      referenceData={referenceData}
      columnDefinitions={columnDefinitions}
      columnSorts={columnSorts}
      onColumnSortChange={onColumnSortChange}
      onRowClick={onRowClick}
      loading={loading}
      isTransitioning={false}
      pageSize={pageSize}
      hasActiveFilters={false}
      hideHeader={false}
      pagination={!loading && pagination && pagination.totalItems > 0 ? pagination : undefined}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        setPageSize(size);
        setPage(1);
      }}
      subtotalRow={<SubtotalRow group={group} visibleColumnIds={visibleColumnIds} />}
    />
  );
}

// ─── Server-paged sibling set ─────────────────────────────────────────

interface ServerGroupLevelProps {
  depth: number;
  /** Raw key path of the parent group; empty at the top level. */
  parentPath: GroupPathSegment[];
  serverLevels: ServerLevelContext;
  referenceData: ProjectsGridReferenceData;
  columnDefinitions: ColumnDefinition[];
  columnSorts: ColumnSort[];
  onColumnSortChange: (sorts: ColumnSort[]) => void;
  onRowClick: (project: Project) => void;
  collapsed: Set<string>;
  onToggleCollapse: (key: string) => void;
  leafFilters?: ProjectFilterParams;
  leafPageSize?: number;
  /** Rendered above the groups once the level has loaded (top level only). */
  renderSummary?: (info: {
    groups: ProjectGroup[];
    totalGroups: number;
    totalProjects: number;
  }) => React.ReactNode;
}

/**
 * Loads and renders one page of one sibling set of groups. Nested levels mount their
 * own instance when their parent is expanded, so only the groups on screen are fetched.
 */
function ServerGroupLevel({
  depth,
  parentPath,
  serverLevels,
  referenceData,
  columnDefinitions,
  columnSorts,
  onColumnSortChange,
  onRowClick,
  collapsed,
  onToggleCollapse,
  leafFilters,
  leafPageSize,
  renderSummary,
}: ServerGroupLevelProps) {
  const { groups, pagination, totals, setPage, setPageSize, loading, failed, retry } =
    useProjectGroupLevel({
      groupBy: serverLevels.groupBy,
      depth,
      parentPath,
      referenceData: serverLevels.groupRefData,
      filters: serverLevels.filters,
      initialPageSize: serverLevels.pageSize,
      enabled: true,
    });

  if (loading && groups.length === 0) {
    return (
      <div className="space-y-2" data-testid="group-level-skeleton">
        {[1, 2, 3].map((i) => (
          <div key={i} className="border border-border rounded-lg overflow-hidden">
            <div className="bg-muted/50 px-4 py-2.5 flex items-center gap-3">
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-5 w-16 rounded-full ml-auto" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (failed) {
    // Reported in place rather than falling back to grouping the whole dataset in the
    // browser: a failure here (e.g. the grouped-tree procedure missing from the
    // database) must not silently turn into every group and row being loaded at once.
    return (
      <div className="flex items-center gap-3 px-4 py-6 text-sm text-muted-foreground">
        <span>Couldn&apos;t load these groups.</span>
        <Button variant="outline" size="sm" onClick={retry}>
          Retry
        </Button>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="px-4 py-6 text-sm text-muted-foreground">No groups found</div>
    );
  }

  return (
    <div>
      {renderSummary?.({
        groups,
        totalGroups: pagination?.totalItems ?? groups.length,
        totalProjects: totals?.count ?? groups.reduce((sum, g) => sum + g.subtotals.count, 0),
      })}

      {groups.map((group) => (
        <GroupBlock
          key={group.key}
          group={group}
          allGroupBy={serverLevels.groupBy}
          referenceData={referenceData}
          columnDefinitions={columnDefinitions}
          columnSorts={columnSorts}
          onColumnSortChange={onColumnSortChange}
          onRowClick={onRowClick}
          collapsed={collapsed}
          onToggleCollapse={onToggleCollapse}
          leafFilters={leafFilters}
          leafPageSize={leafPageSize}
          serverLevels={serverLevels}
        />
      ))}

      {pagination && pagination.totalPages > 1 && (
        <PaginationControls
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          className="px-4 py-2"
        />
      )}
    </div>
  );
}

// Depth-based visual styling — each level gets slightly less prominent header background
const DEPTH_HEADER_BG = [
  'bg-muted/50 hover:bg-muted',           // depth 0 — primary groups
  'bg-muted/30 hover:bg-muted/50',        // depth 1 — sub-groups
  'bg-muted/20 hover:bg-muted/40',        // depth 2 — sub-sub-groups
];

const DEPTH_BORDER_COLOR = [
  'border-border',
  'border-border/70',
  'border-border/50',
];

function GroupBlock({
  group,
  allGroupBy,
  referenceData,
  columnDefinitions,
  columnSorts,
  onColumnSortChange,
  onRowClick,
  collapsed,
  onToggleCollapse,
  leafFilters,
  leafPageSize = 10,
  serverLevels,
}: GroupBlockProps) {
  const isCollapsed = collapsed.has(group.key);

  const visibleColumnIds = useMemo(
    () =>
      columnDefinitions
        .filter((c) => c.visible)
        .sort((a, b) => a.order - b.order)
        .map((c) => c.id),
    [columnDefinitions],
  );

  // Sort projects within this group according to the active column sorts
  const sortedProjects = useMemo(
    () => sortProjectsByColumns(group.projects, columnSorts, referenceData.clients),
    [group.projects, columnSorts, referenceData.clients],
  );

  // Determine if this group has nested sub-groups or is a leaf (renders a flat grid).
  // Server-paged children aren't loaded until expand, so `hasChildren` — not the
  // presence of `subGroups` — decides whether this group is a leaf.
  const hasLoadedSubGroups = group.subGroups !== undefined && group.subGroups.length > 0;
  const hasServerSubGroups = serverLevels !== undefined && group.hasChildren === true;

  // Use the raw key segment (last part) for the badge — strip parent prefix
  const rawKey = group.key.split('::').pop() ?? group.key;

  const headerBg = DEPTH_HEADER_BG[group.depth] ?? DEPTH_HEADER_BG[DEPTH_HEADER_BG.length - 1];
  const borderColor = DEPTH_BORDER_COLOR[group.depth] ?? DEPTH_BORDER_COLOR[DEPTH_BORDER_COLOR.length - 1];

  return (
    <div
      className={cn(
        'border rounded-lg overflow-hidden mb-2',
        borderColor,
        group.depth > 0 && 'ml-6',
      )}
    >
      {/* Group header */}
      <button
        type="button"
        className={cn(
          'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
          headerBg,
          !isCollapsed && 'border-b',
          borderColor,
        )}
        onClick={() => onToggleCollapse(group.key)}
        aria-expanded={!isCollapsed}
      >
        {isCollapsed ? (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        )}

        {/* Badge for status / health / type — uses this group's own groupById */}
        <GroupHeaderBadge groupBy={group.groupById} groupKey={rawKey} />

        {/* Label */}
        <span
          className={cn(
            'font-semibold text-foreground flex-1 truncate',
            group.depth === 0 ? 'text-sm' : 'text-xs',
          )}
        >
          {group.label}
        </span>

        {/* Project count pill */}
        <span className="shrink-0 text-xs font-medium text-muted-foreground bg-background border border-border rounded-full px-2 py-0.5">
          {group.subtotals.count} {group.subtotals.count === 1 ? 'project' : 'projects'}
        </span>

        {/* Quick financial summary — only on top-level groups to avoid clutter */}
        {group.depth === 0 && (
          <GroupHeaderSummary
            group={group}
            columnDefinitions={columnDefinitions}
          />
        )}
      </button>

      {/* Expanded content */}
      {!isCollapsed && (
        <div>
          {hasServerSubGroups ? (
            /* Nested sibling set — fetched one page at a time now that the parent is open */
            <div className="p-2">
              <ServerGroupLevel
                depth={group.depth + 1}
                parentPath={group.groupPath ?? []}
                serverLevels={serverLevels!}
                referenceData={referenceData}
                columnDefinitions={columnDefinitions}
                columnSorts={columnSorts}
                onColumnSortChange={onColumnSortChange}
                onRowClick={onRowClick}
                collapsed={collapsed}
                onToggleCollapse={onToggleCollapse}
                leafFilters={leafFilters}
                leafPageSize={leafPageSize}
              />
            </div>
          ) : hasLoadedSubGroups ? (
            /* Render nested sub-groups recursively */
            <div className="p-2">
              {group.subGroups!.map((subGroup) => (
                <GroupBlock
                  key={subGroup.key}
                  group={subGroup}
                  allGroupBy={allGroupBy}
                  referenceData={referenceData}
                  columnDefinitions={columnDefinitions}
                  columnSorts={columnSorts}
                  onColumnSortChange={onColumnSortChange}
                  onRowClick={onRowClick}
                  collapsed={collapsed}
                  onToggleCollapse={onToggleCollapse}
                  leafFilters={leafFilters}
                  leafPageSize={leafPageSize}
                />
              ))}
            </div>
          ) : group.serverPaginated ? (
            /* Leaf group with server-side rows — load just this group's page */
            <ServerLeafGrid
              group={group}
              leafFilters={leafFilters}
              initialPageSize={leafPageSize}
              referenceData={referenceData}
              columnDefinitions={columnDefinitions}
              columnSorts={columnSorts}
              onColumnSortChange={onColumnSortChange}
              onRowClick={onRowClick}
              visibleColumnIds={visibleColumnIds}
            />
          ) : (
            /* Leaf group — render the flat project grid */
            <ProjectsGrid
              data={sortedProjects}
              referenceData={referenceData}
              columnDefinitions={columnDefinitions}
              columnSorts={columnSorts}
              onColumnSortChange={onColumnSortChange}
              onRowClick={onRowClick}
              loading={false}
              isTransitioning={false}
              hasActiveFilters={false}
              hideHeader={false}
              subtotalRow={
                <SubtotalRow group={group} visibleColumnIds={visibleColumnIds} />
              }
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Group count summary + expand/collapse all ────────────────────────

interface GroupCountSummaryProps {
  /** Groups across every page of the top level, not just the loaded ones. */
  totalGroups: number;
  totalProjects: number;
  /** The groups currently on screen — expand/collapse all acts on these. */
  pageGroups: ProjectGroup[];
  collapsed: Set<string>;
  onCollapsedChange: (collapsed: Set<string>) => void;
}

function GroupCountSummary({
  totalGroups,
  totalProjects,
  pageGroups,
  collapsed,
  onCollapsedChange,
}: GroupCountSummaryProps) {
  const allCollapsed = pageGroups.every((g) => collapsed.has(g.key));
  const allExpanded = pageGroups.every((g) => !collapsed.has(g.key));

  const collapseAll = () => {
    const next = new Set(collapsed);
    for (const group of pageGroups) next.add(group.key);
    onCollapsedChange(next);
  };

  const expandAll = () => {
    const next = new Set(collapsed);
    for (const group of pageGroups) next.delete(group.key);
    onCollapsedChange(next);
  };

  return (
    <div className="flex items-center justify-end gap-3 mb-2 px-6">
      <p className="text-xs text-muted-foreground">
        {totalGroups} {totalGroups === 1 ? 'group' : 'groups'} · {totalProjects} projects total
      </p>
      <div className="flex gap-3 text-xs">
        {!allExpanded && (
          <button type="button" className="text-primary hover:underline" onClick={expandAll}>
            Expand all
          </button>
        )}
        {!allCollapsed && (
          <button type="button" className="text-primary hover:underline" onClick={collapseAll}>
            Collapse all
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────

export interface ProjectsGroupedViewProps {
  groups: ProjectGroup[];
  groupBy: GroupById[];
  referenceData: {
    clients: Client[];
    employees: Employee[];
    tasks: Task[];
    milestones: Milestone[];
    resourceAllocations: ResourceAllocation[];
  };
  columnDefinitions: ColumnDefinition[];
  columnSorts: ColumnSort[];
  onColumnSortChange: (sorts: ColumnSort[]) => void;
  onRowClick: (project: Project) => void;
  loading?: boolean;
  /** List-level filters forwarded to server-paginated leaf row requests */
  leafFilters?: ProjectFilterParams;
  /** Page size used by server-paginated leaf groups */
  leafPageSize?: number;
  /**
   * Set in live mode: the sibling sets are fetched and paged by the server, one level
   * at a time, and `groups` is ignored. Left unset for demo/tour and the client-side
   * fallback, where `groups` already holds the whole tree.
   */
  serverLevels?: ServerLevelContext;
}

export function ProjectsGroupedView({
  groups,
  groupBy,
  referenceData,
  columnDefinitions,
  columnSorts,
  onColumnSortChange,
  onRowClick,
  loading = false,
  leafFilters,
  leafPageSize,
  serverLevels,
}: ProjectsGroupedViewProps) {
  // Track which group keys are collapsed (collapsed = true means rows hidden)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggleCollapse = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Server-paged grouping owns its own loading, empty and pagination states: only the
  // groups on the current page of each sibling set exist client-side.
  if (serverLevels) {
    return (
      <ServerGroupLevel
        depth={0}
        parentPath={[]}
        serverLevels={serverLevels}
        referenceData={referenceData}
        columnDefinitions={columnDefinitions}
        columnSorts={columnSorts}
        onColumnSortChange={onColumnSortChange}
        onRowClick={onRowClick}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapse}
        leafFilters={leafFilters}
        leafPageSize={leafPageSize}
        renderSummary={({ groups: pageGroups, totalGroups, totalProjects }) => (
          <GroupCountSummary
            totalGroups={totalGroups}
            totalProjects={totalProjects}
            pageGroups={pageGroups}
            collapsed={collapsed}
            onCollapsedChange={setCollapsed}
          />
        )}
      />
    );
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="border border-border rounded-lg overflow-hidden">
            <div className="bg-muted/50 px-4 py-2.5 flex items-center gap-3">
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-5 w-16 rounded-full ml-auto" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-lg font-medium text-foreground">No projects found</p>
        <p className="text-sm text-muted-foreground mt-1">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <div>
      <GroupCountSummary
        totalGroups={groups.length}
        totalProjects={groups.reduce((s, g) => s + g.subtotals.count, 0)}
        pageGroups={groups}
        collapsed={collapsed}
        onCollapsedChange={setCollapsed}
      />

      {groups.map((group) => (
        <GroupBlock
          key={group.key}
          group={group}
          allGroupBy={groupBy}
          referenceData={referenceData}
          columnDefinitions={columnDefinitions}
          columnSorts={columnSorts}
          onColumnSortChange={onColumnSortChange}
          onRowClick={onRowClick}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
          leafFilters={leafFilters}
          leafPageSize={leafPageSize}
        />
      ))}
    </div>
  );
}
